import { SUITE_FEATURE_GROUPINGS } from "@silversea-ssc/web-suite-features"
import { SanityQueryResponseOptional } from "./sanity-types"

// use when you're ging to migrate to array-based localization
const ltmp = (name: TemplateStringsArray) =>
    `'${name}': coalesce(${name}[$lang], ${name}["langs"][@._key == $lang][0].value ,${name}["en"], ${name}["langs"][@._key == "en"][0].value)`

const l = (name: TemplateStringsArray) => `'${name}': coalesce(${name}[$lang], ${name}["en"])`

// use when you're ging to migrate to array-based localization
export const larray = (name: TemplateStringsArray) =>
    `'${name}': coalesce(${name}["langs"][@._key == $lang][0].value ,${name}["langs"][@._key == "en"][0].value)`

const sanityImageFragment = `
    _key,
    asset->{
        _id,
        assetId,
        metadata {
         dimensions
        }
      },

    hotspot {
      height,
      width,
      x,
      y,
    },
    crop {
      bottom,
      left,
      right,
      top,
    },
    ${l`alt`},
    ${l`caption`}
`

const sanityImageWithPreviewFragment = `
    _key,
    asset->{
        _id,
        assetId,
        metadata{
            "preview": lqip
        }
      },

    hotspot {
      height,
      width,
      x,
      y,
    },
    crop {
      bottom,
      left,
      right,
      top,
    },
    ${l`alt`},
    ${l`caption`}
`

const cruiseQuery = `*[(_type == "cruise" || _type == "specialVoyage") && cruiseCode == $cruiseCode] {
    _type,
    "cardImage": coalesce(cardImage, card.image),
    cruiseCode,
    'path': metadata.routing.path[$lang],
}[0]`

const cruiseDataQuery = `*[(_type == "cruiseData" || _type == "specialVoyageData")
        && cruiseCode == $cruiseCode
        && (isVisible == true || $allowNotVisible == true)] {
    _type,
    cruiseCode,
    destination -> {
        destinationWebCode,
        'trackingName': name["en"],
        ${l`name`},
        'path': metadata.routing.path[$lang],
    },
    departurePort -> {
        ${l`name`},
        "portImage": slideshow[0]{${sanityImageFragment}},
        data -> {code}
    },
    arrivalPort -> {
        ${l`name`}
    },
    departureDate,
    arrivalDate,
    days,
    cruiseType,
    cruiseGroup,
    comboType,
    ship -> {
        _id,
        name, card { ${l`title`} },
        "version": versions[(validFrom <= ^.^.departureDate)] | order(validFrom)[-1] {
            _key,
            decks[] {
                deckNumber,
                "deckImageURL": deckImage{
                asset->{
                      url
                    },
              }.asset.url
            },
            suites[] {
                "shipVersionKey": ^._key,
                color,
                card { ${l`title`}, ${l`description`}, image },
                configurations[]{
                    code,
                    ${l`shortSizeDescriptionInSquareFeet`},
                    ${l`shortSizeDescriptionInSquareMetres`},
                    ${l`verandaSizeInSquareFeet`},
                    ${l`verandaSizeInSquareMetres`},
                },
                keyFeatures {
                    veranda,
                    livingRoom
                },
                deck[]
            },
            lateralViewImage {
                ${sanityImageWithPreviewFragment}
            },
            ${l`deckPlanDescription`},
            passengerCount,
            crewCount,
            buildYear,
            lastRefurbishmentYear,
            tonnage,

            deckPlanPdf {
                asset -> {
                    url
                }
            },

            diningAreas[] {
                _key,
                card { ${l`title`}, 'trackingName': title["en"] }
            },
            facilities[] {
                _key,
                card { ${l`title`}, 'trackingName': title["en"] }
            }
        }
    }
}[0]`

//const cruiseDataQuery = cruiseDataQueryFor("cruiseData")
//console.debug(cruiseDataQuery)

// const specialVoyageQuery = `*[_type == "specialVoyage" && cruiseCode == $cruiseCode] {
//     cruiseCode,
//     card {
//         image
//     },
//     'path': metadata.routing.path[$lang]
// }[0]`

//const specialVoyageDataQuery = cruiseDataQueryFor("specialVoyageData")

const specialVoyageDestinationQuery = `*[_id == "site-silversea-com"][0]{
  grandVoyagesPage->{
    "title": title[$lang],
    'trackingName': title["en"],
    "path": metadata.routing.path[$lang]
  },
  worldCruisesPage->{
    "title": title[$lang],
    'trackingName': title["en"],
    "path": metadata.routing.path[$lang]
  }
}
`

// TODO: terms and conditions page: find a way to specify which version
const websiteUrls = `
{
    "termsAndConditions": *[_id == "site-silversea-com"]{
        'path': termsAndConditionsExternalPage,
    }[0],
    "findYourCruise": *[_type == "page" && _id == "page-cruise"] {
        "path": metadata.routing.path[$lang]
    }[0]
}
`

const buildCoalesceItem = (name: string): string => {
    return `${name}[$lang], ${name}["langs"][@._key == $lang][0].value`
    // return `${name}[$lang]`
}

const buildCoalesce = (key: string): string =>
    `coalesce(${buildCoalesceItem(`cruiseSpecificContent.${key}`)}, ${buildCoalesceItem(
        `groupSpecificContent.${key}`,
    )}, ${buildCoalesceItem(`genericContent.${key}`)})`

const enhancedBlockContent = (fieldName: string): string => {
    return `
    ${fieldName}[]{
        ...,
        markDefs[]{
            ...,
            _type == "linkToPage" => {
                "path": @.page->metadata.routing.path[$lang]
            }
        }
    }
    `
}

const cancellationPolicyModalSectionContentQuery = `
type,
${l`title`},
${enhancedBlockContent(l`content`)},
${l`disclaimer`},
${l`staticInfographicRefundableLabel`},
${l`staticInfographicFCCNote`}
`

const formDisclaimerContentQuery = `
*[_type == "formDisclaimer" && _id == "formDisclaimer"][0]{
  ${l`disclaimer`},
  overrideDisclaimer[] {
    ${l`content`},
    locationFilters
  }
}`

const penaltiesModalContentQuery = `
 *[_type == "cancellationPolicyModal"]{
        _id,
        cancelledBy[] {${cancellationPolicyModalSectionContentQuery}}
    }[0]`

const inclusionsContentQuery = `
     *[_type == "inclusion"]{
        _id,
        codes,
        icon,
        "infographicTitle": ${buildCoalesce("infographicTitle")},
        "title": ${buildCoalesce("title")},
        "description": ${buildCoalesce("description")},
        "footnote": ${buildCoalesce("footnote")},
        overrideContent[] {
            cruiseGroupFilters,
            "infographicTitle": ${buildCoalesce("infographicTitle")},
            "title": ${buildCoalesce("title")},
            "footnote": ${buildCoalesce("footnote")},
            "description": ${buildCoalesce("description")}
        }
    }`

const faresContentQuery = `
    *[_type == "farePage" && defined(fareCodes)]{
        _id,
        fareCodes,
        "title": card { ${l`title`} }.title,
        ${l`headline`}
    }`

// const featureToggles = `
// *[_type == "site" && _id == "site-silversea-com"] {
//     featureToggles{
//         enableComparableSuiteFeatures
//     }
// }[0].featureToggles`

const cardPromoBadgesContent = `
    *[_type == "promoConfiguration" && defined(code)]{
        code,
        "content": *[_type == "exclusiveOfferPerPromo" && references(^._id)]{${l`title`}, showInCruiseCard, "iconUrl": iconVertical{asset->{url}}.asset.url}[0]
      }
`
const comparableFeaturesKeys = SUITE_FEATURE_GROUPINGS.flatMap((x) => x.features?.map((x) => x.key)).join(",")
export const suiteContentQuery = `*[_type == "ship" && _id == $shipId][0]
{
    "version": versions[(_key == $shipVersionKey)][0]{
        decks[] {
            deckSuiteCategories[]{
                suiteCategory{_ref}
              },
            deckNumber,
            "deckImageURL": deckImage{
                asset->{
                      url
                    },
              }.asset.url
        },
        suites[]{
            category{_ref},
            card { ${l`title`} },
            ${l`description`},
            images[]{${sanityImageFragment}},
            color,
            configurations[] {
                code,
                ${l`shortSizeDescriptionInSquareFeet`},
                ${l`shortSizeDescriptionInSquareMetres`},
                ${l`verandaSizeInSquareFeet`},
                ${l`verandaSizeInSquareMetres`},
                ${l`name`},
                plan{${sanityImageWithPreviewFragment}},
                ${l`configurationInformation`}
              },
            shipSections {
                ...
              },
              deck,
            comparableFeatures {
                _key,
                ${comparableFeaturesKeys}
              }
        }
    }
}`

const caveatFields = SUITE_FEATURE_GROUPINGS.flatMap((x) =>
    x.features
        .filter((f) => f.caveat)
        .map((f) => `${f.key}_caveat`)
        .map((k) => `"${k}": ${k}[$lang]`),
).join(",")

export const suitesComparisonContentQuery = `*[_type == "ship" && _id == $shipId][0]
{
    "version": versions[(_key == $shipVersionKey)][0]{
        decks[] {
            deckNumber
        },
        suites[]{
            _key,
            "name": category->{${l`name`}}.name,
            "image": card.image{${sanityImageFragment}},
            "features": comparableFeatures {
                _key,
                ${comparableFeaturesKeys},
                ${caveatFields}
            },
            configurations[] {
                code,
                ${l`shortSizeDescriptionInSquareFeet`},
                ${l`shortSizeDescriptionInSquareMetres`},
                 ${l`verandaSizeInSquareFeet`},
                ${l`verandaSizeInSquareMetres`},
                plan {
                    ${sanityImageFragment}
                }
            },
            deck,
            location,
            plan,
            shipSections
        }
    }
}`

export const publicAreasContentQuery = (
    areaType: "diningAreas" | "facilities",
) => `*[_type == "ship" && _id == $shipId][0]
{
    "version": versions[(_key == $shipVersionKey)][0]{
        'publicAreas': ${areaType}[]{
            _key,
            card { ${l`title`}, 'trackingName': title["en"] },
            ${l`description`},
            images[]{${sanityImageFragment}}
        }
    }
}`

const termsAndConditionsModalQuery = `*[_type == "termsAndConditionsSummary"][0]
{
    ${larray`modalTitle`},
    ${enhancedBlockContent(larray`modalContent`)}
}`

const dynamicFAQQuery = `*[_type == "dynamicFAQ"]|order(orderRank){
    "_key": _id,    
    visibleInSteps,
    "titleEN": title["langs"][@._key == "en"][0].value,
    ${larray`title`},
    ${larray`content`}
}`

export type SanityQueryContent = keyof SanityQueryResponseOptional
const extraQueries: Record<SanityQueryContent, string> = {
    penaltiesModalContent: penaltiesModalContentQuery,
}

const compress = (q: string): string => q.replace(/[ \t]/g, "")

export const buildSanityContentQuery = (includes: readonly SanityQueryContent[] = []): string => {
    const q = buildParallelSanityContentQuery(includes)

    return compress(mergeToSanityQuery(q))
}

export const buildParallelSanityContentQuery = (
    includes: readonly SanityQueryContent[] = [],
): Record<string, string> => {
    const extra = Object.fromEntries(includes.map((key) => [key, extraQueries[key]]))

    const cruiseCodeDependentQueries = {
        cruise: cruiseQuery,
        cruiseData: cruiseDataQuery,
        specialCruiseDestination: specialVoyageDestinationQuery,
        websiteUrls: websiteUrls,
        cardPromoBadgesContent: cardPromoBadgesContent,
        faresContent: faresContentQuery,
        formDisclaimer: formDisclaimerContentQuery,
        termsAndConditionsModal: termsAndConditionsModalQuery,
        dynamicFAQ: dynamicFAQQuery,
        inclusionsContent: inclusionsContentQuery,
        ...(includes.length > 0 && extra),
    }

    return cruiseCodeDependentQueries
}

export const mergeToSanityQuery = (items: Record<string, string>): string =>
    `{
        ${Object.keys(items)
            //.map((key) => `"${key}": ${items[key]}, "time": $time`)
            .map((key) => `"${key}": ${items[key]}`)
            .join(",")}
    }`
